import type { CierreDeRespuesta, PieDeRespuesta as Pie } from "./api";

/**
 * Debajo de cada respuesta: cómo acabó, y con qué.
 *
 * El ESTADO era dos líneas al final del texto —«ESTADO: HECHO» y
 * «verificado_por: leer_web»— que se leían como código. Ahora es una pastilla
 * con palabras, y a su lado lo que costó: modelo, esfuerzo, herramientas,
 * duración y coste. Discreto a propósito: se mira cuando hace falta, no tapa
 * lo que dijo.
 */

const ETIQUETA: Record<string, { texto: string; clase: string }> = {
  HECHO: { texto: "Hecho", clase: "hecho" },
  "HECHO-SIN-COMPROBAR": { texto: "Hecho, sin comprobar", clase: "sin-comprobar" },
  "NO-HECHO": { texto: "No hecho", clase: "no-hecho" },
  "PENDIENTE-DE-APROBACION": { texto: "Pendiente de tu aprobación", clase: "pendiente" },
  /* Lo pone el motor cuando el turno dejó trabajo encolado: no está hecho, está corriendo. */
  "EN-MARCHA": { texto: "En marcha", clase: "pendiente" },
};

const ESFUERZO: Record<string, string> = {
  high: "alto",
  medium: "medio",
  low: "bajo",
  none: "sin razonar",
};

function duracion(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  return `${Math.floor(s / 60)} min ${s % 60} s`;
}

/** Céntimos: los de DeepSeek son fracciones, y un «0 ¢» mentiría. */
function coste(centimos: number): string {
  if (centimos >= 100) {
    return (centimos / 100).toLocaleString("es-ES", { style: "currency", currency: "EUR" });
  }
  return `${centimos.toLocaleString("es-ES", { maximumSignificantDigits: 2 })} ¢`;
}

export function PieDeRespuesta({ pie, cierre }: { pie?: Pie | undefined; cierre: CierreDeRespuesta | null }) {
  const etiqueta = cierre ? (ETIQUETA[cierre.estado] ?? { texto: cierre.estado, clase: "" }) : null;

  const totalHerramientas = pie?.herramientas.reduce((n, h) => n + h.veces, 0) ?? 0;
  const detalleHerramientas = pie?.herramientas
    .map((h) => (h.veces > 1 ? `${h.nombre} ×${h.veces}` : h.nombre))
    .join(", ");

  const partes = pie
    ? [
        pie.modelo,
        pie.esfuerzo ? (ESFUERZO[pie.esfuerzo] ?? pie.esfuerzo) : null,
        totalHerramientas > 0
          ? `${totalHerramientas} ${totalHerramientas === 1 ? "herramienta" : "herramientas"}`
          : null,
        pie.duracionMs !== null ? duracion(pie.duracionMs) : null,
        pie.costeCentimos !== null ? coste(pie.costeCentimos) : null,
      ].filter((x): x is string => Boolean(x))
    : [];

  if (!etiqueta && partes.length === 0) return null;

  return (
    <div className="pie-respuesta">
      {etiqueta && (
        <span
          className={`cierre ${etiqueta.clase}`}
          title={cierre?.porque ? `Rebajado por el sistema: ${cierre.porque}` : undefined}
        >
          {etiqueta.texto}
          {cierre?.estado === "HECHO" && cierre.verificadoPor
            ? ` · comprobado con ${cierre.verificadoPor}`
            : ""}
        </span>
      )}
      {partes.length > 0 && (
        <span className="datos" title={detalleHerramientas || undefined}>
          {partes.join(" · ")}
        </span>
      )}
    </div>
  );
}
